
import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { MappedLeadData, SignalPost, SignalAnalysis } from '../../types';
import { SocialIcon } from '../../utils/icons';
import { ResearchSummarySections } from '../common/ResearchSummarySections';
import { Card, CardHeader, SimpleTabContent, SectionLabel, Tag } from '../common/Atomic';
import { getResearchSummarySectionsForTab } from '../../utils/researchSummary';

interface SignalsTabProps {
  data: MappedLeadData;
}

const SignalInsightCard: React.FC<{ post: SignalPost; analysis?: SignalAnalysis; source: string }> = ({ post, analysis, source }) => {
    const [showRaw, setShowRaw] = useState(false);
    
    // Fallback if no analysis exists
    if (!analysis) {
        return <PostCard post={post} source={source} />;
    }

    const relevanceColor = analysis.relevance_score > 90 ? 'text-emerald-400' : analysis.relevance_score > 70 ? 'text-[#15FBFF]' : 'text-gray-400';
    const sentimentBg = analysis.sentiment === 'positive' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : analysis.sentiment === 'negative' ? 'bg-red-500/10 border-red-500/20 text-red-400' : 'bg-gray-500/10 border-gray-500/20 text-gray-400';

    return (
        <div className="rounded-2xl bg-white/[0.03] relative">
             {/* Left Border Indicator based on Strategic Value */}
             <div className={`absolute left-0 top-0 bottom-0 w-1 ${analysis.strategic_value.toLowerCase().includes('high') ? 'bg-[#5260FF]' : 'bg-gray-700'}`}></div>

             <div className="p-6 pl-8">
                 {/* Header */}
                 <div className="flex items-start justify-between mb-6">
                     <div>
                         <div className="flex items-center gap-3 mb-2">
                             <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 flex items-center gap-1">
                                <SocialIcon platform={source.replace('posts_', '')} className="w-3 h-3" />
                                {source.replace('posts_', '')} Signal
                             </span>
                             <span className="text-gray-600">•</span>
                             <span className="text-[10px] text-gray-500">{post.timeSincePosted || 'Recently'}</span>
                         </div>
                         <h3 className="text-lg font-bold text-white leading-tight">{analysis.headline}</h3>
                     </div>
                     <div className="flex flex-col items-end">
                         <div className={`text-2xl font-bold ${relevanceColor}`}>{analysis.relevance_score}%</div>
                         <div className="text-[10px] uppercase tracking-wide text-gray-500 font-bold">Relevance</div>
                     </div>
                 </div>

                 {/* Analysis Grid */}
                 <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                      {/* Insight & Strategy (2/3) */}
                      <div className="lg:col-span-2 space-y-5">
                           {/* Summary */}
                           <div className="bg-white/[0.02] rounded-xl p-4">
                               <div className="flex gap-2 mb-2">
                                   {analysis.topics.map((t, i) => (
                                       <Tag key={i} label={t} color="text-[#5260FF] bg-[#5260FF]/5 border-[#5260FF]/10" />
                                   ))}
                                   <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${sentimentBg}`}>
                                       {analysis.sentiment}
                                   </span>
                               </div>
                               <p className="text-sm text-gray-300 leading-relaxed">{analysis.summary}</p>
                           </div>

                           {/* Actionable Hook */}
                           <div className="relative pl-4 border-l-2 border-[#15FBFF]">
                               <SectionLabel label="Actionable Hook" className="mb-1 text-[#15FBFF]" />
                               <p className="text-sm text-white font-medium italic">"{analysis.actionable_insight}"</p>
                           </div>
                      </div>

                      {/* Strategic Meta (1/3) */}
                      <div className="space-y-3">
                          <div className="p-4 bg-white/[0.02] rounded-xl h-full flex flex-col justify-center">
                               <SectionLabel label="Strategic Value" />
                               <div className="text-white font-bold">{analysis.strategic_value}</div>
                          </div>
                      </div>
                 </div>

                 {/* Raw Context Toggle */}
                 <div className="border-t border-white/5 pt-4">
                     <button 
                        onClick={() => setShowRaw(!showRaw)}
                        className="flex items-center gap-2 text-xs text-gray-500 hover:text-white transition-colors"
                     >
                         <svg className={`w-4 h-4 transition-transform ${showRaw ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/></svg>
                         {showRaw ? 'Hide Source Context' : 'View Source Context'}
                     </button>
                     
                     {showRaw && (
                         <div className="mt-4 pt-4 animate-fadeIn">
                              <PostCard post={post} source={source} isEmbedded={true} />
                         </div>
                     )}
                 </div>
             </div>
        </div>
    );
};

const PostCard: React.FC<{ post: SignalPost; source: string; isEmbedded?: boolean }> = ({ post, source, isEmbedded = false }) => {
    const isRepost = post.isActivity && post.activityDescription?.includes('reposted');
    const displayAuthor = isRepost ? post.activityOfUser : post.author;

    return (
        <div className={`rounded-2xl bg-white/[0.02] ${isEmbedded ? '' : 'mb-6'}`}>
            {/* Context Header */}
            {isRepost && post.activityDescription && (
                <div className="px-4 py-2 bg-white/5 border-b border-white/5 flex items-center gap-2 text-xs text-gray-400">
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
                    <span className="font-medium">{post.activityDescription}</span>
                </div>
            )}

            <div className="p-5">
                {/* Post Header */}
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full overflow-hidden bg-black border border-white/10">
                            {displayAuthor?.picture ? (
                                <img src={displayAuthor.picture} alt="Author" className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-500 text-xs bg-[#222]">
                                    {displayAuthor?.firstName?.[0]}
                                </div>
                            )}
                        </div>
                        <div>
                            <div className="text-white font-bold text-sm hover:underline cursor-pointer">
                                {displayAuthor?.firstName} {displayAuthor?.lastName}
                            </div>
                            {displayAuthor?.occupation && (
                                <div className="text-xs text-gray-500 line-clamp-1">{displayAuthor.occupation}</div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Content Body */}
                <div className="mb-4">
                    <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap break-words">
                        {post.text}
                    </p>
                </div>

                {/* Images */}
                {post.images && post.images.length > 0 && (
                    <div className={`rounded-xl overflow-hidden border border-white/5 mb-4 ${post.images.length > 1 ? 'grid grid-cols-2 gap-0.5' : ''}`}>
                        {post.images.map((img, i) => (
                            <img key={i} src={img} alt="Post Content" className="w-full h-auto object-cover max-h-[400px]" />
                        ))}
                    </div>
                )}

                {/* Stats */}
                <div className="flex items-center justify-between text-xs text-gray-500 border-t border-white/5 pt-3">
                    <div className="flex items-center gap-1">
                        <span>{post.numLikes || 0} likes</span>
                    </div>
                    <div className="flex gap-3">
                        <span>{post.numComments || 0} comments</span>
                        <span>{post.numShares || 0} reposts</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const SignalsTab: React.FC<SignalsTabProps> = ({ data }) => {
    const activities = data.recentActivities || [];
    const researchSections = getResearchSummarySectionsForTab(data, 'signals');

    // Extract engagement intelligence from rawProfileIntel
    const profileIntel = data.rawProfileIntel || {};
    const socialIntelligence = profileIntel.social_intelligence || {};
    const networkIntelligence = profileIntel.network_intelligence || {};
    const contentAnalysis = socialIntelligence.content_analysis || {};
    const contentSummary = contentAnalysis.content_summary || {};
    const internalNetwork = networkIntelligence.internal_network || {};
    const externalNetwork = networkIntelligence.external_network || {};
    const engagementMetrics = socialIntelligence.engagement_metrics || {};

    return (
        <SimpleTabContent>
            <div className="space-y-4">
                <ResearchSummarySections
                    title="Signal Research"
                    sections={researchSections}
                    tab="signals"
                />

                {/* Engagement Intelligence Section */}
                {contentSummary && Object.keys(contentSummary).length > 0 && (
                    <Card>
                        <CardHeader
                            title="Engagement Intelligence"
                            icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"/></svg>}
                            iconColor="text-purple-400"
                        />

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                            {contentSummary.primary_focus && (
                                <div className="p-4 bg-white/[0.02] rounded-xl">
                                    <SectionLabel label="Primary Focus" />
                                    <div className="text-white font-medium text-sm">{contentSummary.primary_focus}</div>
                                </div>
                            )}
                            {contentSummary.secondary_focus && (
                                <div className="p-4 bg-white/[0.02] rounded-xl">
                                    <SectionLabel label="Secondary Focus" />
                                    <div className="text-white font-medium text-sm">{contentSummary.secondary_focus}</div>
                                </div>
                            )}
                            {contentSummary.posting_frequency && (
                                <div className="p-4 bg-white/[0.02] rounded-xl">
                                    <SectionLabel label="Posting Frequency" />
                                    <div className="text-white font-medium text-sm capitalize">{contentSummary.posting_frequency}</div>
                                </div>
                            )}
                            {contentSummary.engagement_style && (
                                <div className="p-4 bg-white/[0.02] rounded-xl">
                                    <SectionLabel label="Engagement Style" />
                                    <div className="text-white font-medium text-sm capitalize">{contentSummary.engagement_style}</div>
                                </div>
                            )}
                            {contentSummary.best_engagement_window && (
                                <div className="p-4 bg-white/[0.02] rounded-xl">
                                    <SectionLabel label="Best Engagement Window" />
                                    <div className="text-white font-medium text-sm">{contentSummary.best_engagement_window}</div>
                                </div>
                            )}
                            {contentSummary.conversation_readiness && (
                                <div className="p-4 bg-white/[0.02] rounded-xl">
                                    <SectionLabel label="Conversation Readiness" />
                                    <div className={`text-sm font-medium capitalize ${
                                        contentSummary.conversation_readiness === 'high' ? 'text-emerald-400' :
                                        contentSummary.conversation_readiness === 'low' ? 'text-red-400' :
                                        'text-yellow-400'
                                    }`}>{contentSummary.conversation_readiness}</div>
                                </div>
                            )}
                        </div>

                        {(contentSummary.hot_topics || contentSummary.pain_themes || contentSummary.aspiration_themes) && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                                {contentSummary.hot_topics && contentSummary.hot_topics.length > 0 && (
                                    <div className="p-4 bg-white/[0.02] rounded-xl">
                                        <SectionLabel label="Hot Topics" />
                                        <div className="flex flex-wrap gap-2 mt-2">
                                            {contentSummary.hot_topics.map((topic: string, i: number) => (
                                                <Tag key={i} label={topic} color="text-purple-400 bg-purple-500/10 border-purple-500/20" />
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {contentSummary.pain_themes && contentSummary.pain_themes.length > 0 && (
                                    <div className="p-4 bg-white/[0.02] rounded-xl">
                                        <SectionLabel label="Pain Themes" />
                                        <div className="flex flex-wrap gap-2 mt-2">
                                            {contentSummary.pain_themes.map((theme: string, i: number) => (
                                                <Tag key={i} label={theme} color="text-red-400 bg-red-500/10 border-red-500/20" />
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {contentSummary.aspiration_themes && contentSummary.aspiration_themes.length > 0 && (
                                    <div className="p-4 bg-white/[0.02] rounded-xl">
                                        <SectionLabel label="Aspiration Themes" />
                                        <div className="flex flex-wrap gap-2 mt-2">
                                            {contentSummary.aspiration_themes.map((theme: string, i: number) => (
                                                <Tag key={i} label={theme} color="text-emerald-400 bg-emerald-500/10 border-emerald-500/20" />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </Card>
                )}

                {/* Network & Engagement Signals Section */}
                {(externalNetwork && Object.keys(externalNetwork).length > 0) || (engagementMetrics && Object.keys(engagementMetrics).length > 0) ? (
                    <Card>
                        <CardHeader
                            title="Network & Engagement Signals"
                            icon={<Icon icon="bx:network-chart" className="h-5 w-5" aria-hidden="true" />}
                            iconColor="text-amber-400"
                        />

                        <div className="space-y-6 mt-4">
                            {/* Key Engagers Section */}
                            {(externalNetwork.job_titles || externalNetwork.seniority || externalNetwork.companies) && (
                                <div className="p-6 rounded-xl bg-white/[0.02]">
                                    <div className="flex items-center gap-2 mb-4">
                                        <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
                                        </svg>
                                        <h4 className="text-white font-bold text-sm">Key Engagers</h4>
                                    </div>

                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                        {/* Job Titles */}
                                        {externalNetwork.job_titles && Object.keys(externalNetwork.job_titles).length > 0 && (
                                            <div className="p-4 bg-white/[0.02] rounded-xl">
                                                <SectionLabel label="Job Titles" />
                                                <div className="space-y-2 mt-2">
                                                    {Object.entries(externalNetwork.job_titles).map(([title, percentage]: any, i: number) => (
                                                        <div key={i} className="flex items-center justify-between text-sm">
                                                            <span className="text-gray-300">{title}</span>
                                                            <span className="text-amber-400 font-bold">{percentage}%</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Seniority */}
                                        {externalNetwork.seniority && Object.keys(externalNetwork.seniority).length > 0 && (
                                            <div className="p-4 bg-white/[0.02] rounded-xl">
                                                <SectionLabel label="Seniority" />
                                                <div className="space-y-2 mt-2">
                                                    {Object.entries(externalNetwork.seniority).map(([level, percentage]: any, i: number) => (
                                                        <div key={i} className="flex items-center justify-between text-sm">
                                                            <span className="text-gray-300">{level}</span>
                                                            <span className="text-amber-400 font-bold">{percentage}%</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Companies */}
                                        {externalNetwork.companies && externalNetwork.companies.length > 0 && (
                                            <div className="p-4 bg-white/[0.02] rounded-xl">
                                                <SectionLabel label="Companies" />
                                                <div className="flex flex-wrap gap-2 mt-2">
                                                    {externalNetwork.companies.map((company: string, i: number) => (
                                                        <Tag key={i} label={company} color="text-amber-400 bg-amber-500/10 border-amber-500/20" />
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Relationship Map Section */}
                            {(externalNetwork.frequent_tags || externalNetwork.community_affiliations || externalNetwork.peer_network) && (
                                <div className="p-6 rounded-xl bg-white/[0.02]">
                                    <div className="flex items-center gap-2 mb-4">
                                        <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"/>
                                        </svg>
                                        <h4 className="text-white font-bold text-sm">Relationship Map</h4>
                                    </div>

                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                        {/* Frequent Tags */}
                                        {externalNetwork.frequent_tags && externalNetwork.frequent_tags.length > 0 && (
                                            <div className="p-4 bg-white/[0.02] rounded-xl">
                                                <SectionLabel label="Frequent Tags" />
                                                <div className="flex flex-wrap gap-2 mt-2">
                                                    {externalNetwork.frequent_tags.map((tag: string, i: number) => (
                                                        <Tag key={i} label={tag} color="text-purple-400 bg-purple-500/10 border-purple-500/20" />
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Community Affiliations */}
                                        {externalNetwork.community_affiliations && externalNetwork.community_affiliations.length > 0 && (
                                            <div className="p-4 bg-white/[0.02] rounded-xl">
                                                <SectionLabel label="Community Affiliations" />
                                                <div className="flex flex-wrap gap-2 mt-2">
                                                    {externalNetwork.community_affiliations.map((affiliation: string, i: number) => (
                                                        <Tag key={i} label={affiliation} color="text-purple-400 bg-purple-500/10 border-purple-500/20" />
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Peer Network */}
                                        {externalNetwork.peer_network && (
                                            <div className="p-4 bg-white/[0.02] rounded-xl">
                                                <SectionLabel label="Peer Network" />
                                                <p className="text-sm text-gray-300 mt-2 leading-relaxed">{externalNetwork.peer_network}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Engagement Health Section */}
                            {engagementMetrics && Object.keys(engagementMetrics).length > 0 && (
                                <div className="p-6 rounded-xl bg-white/[0.02]">
                                    <div className="flex items-center gap-2 mb-4">
                                        <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                                        </svg>
                                        <h4 className="text-white font-bold text-sm">Engagement Health</h4>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                        {/* Average Engagement per Post */}
                                        {engagementMetrics.avg_engagement_per_post && (
                                            <div className="p-4 bg-white/[0.02] rounded-xl">
                                                <SectionLabel label="Avg. Engagement per Post" />
                                                <div className="text-white font-medium text-sm mt-1">
                                                    {typeof engagementMetrics.avg_engagement_per_post === 'object' ? (
                                                        <div className="space-y-1">
                                                            {engagementMetrics.avg_engagement_per_post.likes && (
                                                                <div className="text-xs"><span className="text-gray-400">Likes:</span> <span className="text-emerald-400">{engagementMetrics.avg_engagement_per_post.likes}</span></div>
                                                            )}
                                                            {engagementMetrics.avg_engagement_per_post.comments && (
                                                                <div className="text-xs"><span className="text-gray-400">Comments:</span> <span className="text-emerald-400">{engagementMetrics.avg_engagement_per_post.comments}</span></div>
                                                            )}
                                                            {engagementMetrics.avg_engagement_per_post.shares && (
                                                                <div className="text-xs"><span className="text-gray-400">Shares:</span> <span className="text-emerald-400">{engagementMetrics.avg_engagement_per_post.shares}</span></div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="text-emerald-400">{engagementMetrics.avg_engagement_per_post}</span>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Comment/Like Ratio */}
                                        {engagementMetrics.comment_like_ratio && (
                                            <div className="p-4 bg-white/[0.02] rounded-xl">
                                                <SectionLabel label="Comment/Like Ratio" />
                                                <div className="text-emerald-400 font-bold text-lg mt-1">{engagementMetrics.comment_like_ratio}</div>
                                                {engagementMetrics.conversation_depth && (
                                                    <div className="text-xs text-gray-400 mt-1">{engagementMetrics.conversation_depth}</div>
                                                )}
                                            </div>
                                        )}

                                        {/* Share Rate */}
                                        {engagementMetrics.share_rate && (
                                            <div className="p-4 bg-white/[0.02] rounded-xl">
                                                <SectionLabel label="Share Rate" />
                                                <div className="text-emerald-400 font-bold text-lg mt-1">{engagementMetrics.share_rate}</div>
                                            </div>
                                        )}

                                        {/* Response Rate */}
                                        {engagementMetrics.response_rate && (
                                            <div className="p-4 bg-white/[0.02] rounded-xl">
                                                <SectionLabel label="Response Rate" />
                                                <div className="text-emerald-400 font-bold text-lg mt-1">{engagementMetrics.response_rate}</div>
                                                {engagementMetrics.response_behavior && (
                                                    <div className="text-xs text-gray-400 mt-1">{engagementMetrics.response_behavior}</div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </Card>
                ) : null}

                {/* Recent Activities */}
                <Card>
                    <CardHeader
                        title="Signal Intelligence Stream"
                        icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>}
                        iconColor="text-[#15FBFF]"
                    />

                    {activities.length === 0 ? (
                         <div className="text-center py-12 text-gray-500">
                             <Icon icon="bx:bxs-inbox" className="mx-auto mb-3 h-10 w-10 text-gray-600" aria-hidden="true" />
                             No recent activities found.
                         </div>
                    ) : (
                        <div className="mt-4 space-y-6">
                             {activities.map((activity) => (
                                 <SignalInsightCard
                                    key={activity.id}
                                    post={activity.data}
                                    analysis={activity.analysis}
                                    source={activity.source}
                                 />
                             ))}
                        </div>
                    )}
                </Card>
            </div>
        </SimpleTabContent>
    );
};
